import axios, { AxiosInstance, AxiosResponse } from 'axios';

export interface Game {
  game: string;
  game_type: string;
  icon_url: string;
}

export interface CreateGameRequest {
  game: string;
  game_type: string;
  icon_url?: string;
  company: string;
}

export interface DeleteGameRequest {
  game: string;
  game_type: string;
  company: string;
}

export interface GamesResponse {
  games: Game[];
}

export interface Registration {
  date: string;
  players: number;
}

export interface DownloadCustomRequest {
  startDate: string;
  endDate: string;
}

export interface AggregatedRegistration {
  date: string;
  totalPlayers: number;
  gamesCount: number;
}

export interface GameStatistics {
  game: string;
  game_type: string;
  totalPlayers: number;
  todayPlayers: number;
  lastUpdateDate: string;
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      timeout: 30000, // 30 seconds timeout
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor - adds headers to all requests
    this.api.interceptors.request.use(
      (config) => {
        console.log('📤 API Request:', {
          method: config.method?.toUpperCase(),
          url: config.url,
          baseURL: config.baseURL,
          fullURL: `${config.baseURL}${config.url}`,
          headers: {
            'Content-Type': config.headers['Content-Type'],
            'Accept': config.headers['Accept'],
            'Authorization': config.headers['Authorization'] ? 
              `Bearer [${String(config.headers['Authorization']).substring(7, 27)}...]` : '[MISSING]'
          },
          allHeaders: Object.keys(config.headers).reduce((acc, key) => {
            acc[key] = key.toLowerCase() === 'authorization' ? 
              (config.headers[key] ? '[PRESENT]' : '[MISSING]') : config.headers[key];
            return acc;
          }, {} as any),
          params: config.params
        });

        // Ensure required headers are always present
        if (!config.headers['Content-Type']) {
          config.headers['Content-Type'] = 'application/json';
        }
        if (!config.headers['Accept']) {
          config.headers['Accept'] = 'application/json';
        }

        // Ensure Authorization header is properly formatted
        const authHeader = config.headers['Authorization'];
        if (authHeader && typeof authHeader === 'string' && !authHeader.startsWith('Bearer ')) {
          config.headers['Authorization'] = `Bearer ${authHeader}`;
        }

        // Check for common header case variations and normalize
        const authHeaders = ['authorization', 'Authorization', 'AUTHORIZATION'];
        authHeaders.forEach(header => {
          if (config.headers[header] && header !== 'Authorization') {
            config.headers['Authorization'] = config.headers[header];
            delete config.headers[header];
          }
        });

        // Note: CORS headers are handled by the server, not the client
        // Removing client-side CORS headers as they can interfere with requests

        return config;
      },
      (error) => {
        console.error('❌ Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor - handles responses and errors globally
    this.api.interceptors.response.use(
      (response) => {
        console.log('📥 API Response:', {
          status: response.status,
          statusText: response.statusText,
          url: response.config.url,
          data: response.data
        });
        return response;
      },
      (error) => {
        console.error('❌ API Response Error:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url,
          data: error.response?.data,
          message: error.message
        });

        // Handle specific error cases
        if (error.response?.status === 401) {
          console.error('🚫 Authentication failed - token may be invalid or expired');
          // Could dispatch a logout action here if using Redux/Context
        } else if (error.response?.status === 403) {
          console.error('🚫 Access forbidden - insufficient permissions');
        } else if (error.response?.status === 404) {
          console.error('🔍 Resource not found');
        } else if (error.response?.status >= 500) {
          console.error('🔥 Server error - backend issue');
        } else if (error.code === 'ECONNABORTED') {
          console.error('⏰ Request timeout');
        }

        return Promise.reject(error);
      }
    );
  }

  setAuthToken(token: string) {
    console.log('🔑 Setting auth token:', token ? `${token.substring(0, 20)}...` : 'No token');
    
    // Set the authorization header
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // Also set it on the instance defaults for extra safety
    this.api.defaults.headers['Authorization'] = `Bearer ${token}`;
    
    // Verify it was set correctly
    const commonAuth = this.api.defaults.headers.common['Authorization'];
    const instanceAuth = this.api.defaults.headers['Authorization'];
    
    console.log('✅ Auth header verification:', {
      common: !!commonAuth,
      instance: !!instanceAuth,
      commonValue: commonAuth && typeof commonAuth === 'string' ? 
        `${commonAuth.substring(0, 20)}...` : 'MISSING',
      instanceValue: instanceAuth && typeof instanceAuth === 'string' ? 
        `${instanceAuth.substring(0, 20)}...` : 'MISSING'
    });
  }

  removeAuthToken() {
    console.log('🚫 Removing auth token');
    delete this.api.defaults.headers.common['Authorization'];
    delete this.api.defaults.headers['Authorization'];
    
    // Also remove any case variations
    delete this.api.defaults.headers.common['authorization'];
    delete this.api.defaults.headers['authorization'];
  }

  // Check if auth token is present
  hasAuthToken(): boolean {
    return !!this.api.defaults.headers.common['Authorization'];
  }

  // Get current auth header for debugging
  getCurrentAuthHeader(): string | undefined {
    return this.api.defaults.headers.common['Authorization'] as string;
  }

  // Debug method to check all auth-related headers
  debugAuthState(): void {
    console.log('🔍 Auth Debug State:', {
      hasToken: this.hasAuthToken(),
      commonHeader: this.api.defaults.headers.common['Authorization'] ? 'PRESENT' : 'MISSING',
      instanceHeader: this.api.defaults.headers['Authorization'] ? 'PRESENT' : 'MISSING',
      commonValue: this.getCurrentAuthHeader() ? 
        `${this.getCurrentAuthHeader()?.substring(0, 30)}...` : 'NONE',
      allCommonHeaders: Object.keys(this.api.defaults.headers.common),
      allInstanceHeaders: Object.keys(this.api.defaults.headers)
    });
  }

  // Get games for a specific company
  async getGames(company: string = 'coop'): Promise<Game[]> {
    // Check if auth token is present before making the request
    if (!this.hasAuthToken()) {
      console.warn('⚠️ No auth token present when calling getGames');
      throw new Error('Authentication required. Please login first.');
    }

    console.log('🎯 Making getGames request with auth token present');
    
    try {
      const response: AxiosResponse<Game[] | GamesResponse> = await this.api.get('/games', {
        params: { company }
      });
      
      // Handle both response formats: Game[] or {games: Game[]}
      let gamesData: Game[];
      if (Array.isArray(response.data)) {
        gamesData = response.data;
      } else if (response.data && typeof response.data === 'object' && 'games' in response.data) {
        gamesData = (response.data as GamesResponse).games || [];
      } else {
        console.warn('⚠️ Unexpected API response format:', response.data);
        gamesData = [];
      }
      
      return gamesData;
    } catch (error: any) {
      // Additional error handling specific to games endpoint
      if (error.response?.status === 404) {
        // When no games are found, return empty array instead of throwing error
        console.log(`ℹ️ No games found for company: ${company}, returning empty array`);
        return [];
      }
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Token may be expired or invalid.');
      }
      throw error;
    }
  }

  // Get registrations for a specific game
  async getRegistrations(game: string, gameType: string, company: string): Promise<Registration[]> {
    // Check if auth token is present before making the request
    if (!this.hasAuthToken()) {
      console.warn('⚠️ No auth token present when calling getRegistrations');
      console.warn('📍 This means the component called the API before authentication was ready');
      throw new Error('Authentication required. Please login first.');
    }

    console.log('🎯 Making getRegistrations request with auth token present');
    console.log('🔍 Auth token preview:', this.getCurrentAuthHeader()?.substring(0, 30) + '...');
    
    try {
      // Ensure lowercase for consistency
      const lowerGame = game.toLowerCase();
      const lowerGameType = gameType.toLowerCase();
      const lowerCompany = company.toLowerCase();
      
      const response: AxiosResponse<Registration[]> = await this.api.get('/registrations', {
        params: {
          game: lowerGame,
          gameType: lowerGameType,
          company: lowerCompany
        }
      });
      return response.data;
    } catch (error: any) {
      // Additional error handling specific to registrations endpoint
      if (error.response?.status === 404) {
        // When no registrations are found, return empty array instead of throwing error
        console.log(`ℹ️ No registrations found for game: ${game} (${gameType}) in company: ${company}, returning empty array`);
        return [];
      }
      console.error('Error fetching registrations:', error);
      throw error;
    }
  }

  // Download CSV for a specific date
  async downloadSingleDateCSV(game: string, gameType: string, date: string, company: string): Promise<Blob> {
    // Check if auth token is present before making the request
    if (!this.hasAuthToken()) {
      console.warn('⚠️ No auth token present when calling downloadSingleDateCSV');
      throw new Error('Authentication required. Please login first.');
    }

    console.log('🎯 Making downloadSingleDateCSV request with auth token present');
    
    try {
      // Ensure lowercase for consistency
      const lowerGame = game.toLowerCase();
      const lowerGameType = gameType.toLowerCase();
      const lowerCompany = company.toLowerCase();
      
      const response = await this.api.get('/download', {
        params: {
          game: lowerGame,
          gameType: lowerGameType,
          date: date,
          company: lowerCompany
        },
        responseType: 'blob',
        headers: {
          'Accept': 'text/csv',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading single date CSV:', error);
      throw error;
    }
  }

  // Download CSV for custom date range
  async downloadCustomRangeCSV(
    game: string,
    gameType: string,
    payload: DownloadCustomRequest,
    company: string
  ): Promise<Blob> {
    // Check if auth token is present before making the request
    if (!this.hasAuthToken()) {
      console.warn('⚠️ No auth token present when calling downloadCustomRangeCSV');
      throw new Error('Authentication required. Please login first.');
    }

    console.log('🎯 Making downloadCustomRangeCSV request with auth token present');
    
    try {
      // Ensure lowercase for consistency
      const lowerGame = game.toLowerCase();
      const lowerGameType = gameType.toLowerCase();
      const lowerCompany = company.toLowerCase();
      
      const response = await this.api.post('/download/custom', {
        ...payload,
        game: lowerGame,
        gameType: lowerGameType,
        company: lowerCompany
      }, {
        responseType: 'blob',
        headers: {
          'Accept': 'text/csv',
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading custom range CSV:', error);
      throw error;
    }
  }

  // Create a new game
  async createGame(gameData: CreateGameRequest): Promise<Game> {
    // Check if auth token is present before making the request
    if (!this.hasAuthToken()) {
      console.warn('⚠️ No auth token present when calling createGame');
      throw new Error('Authentication required. Please login first.');
    }

    console.log('🎯 Making createGame request with auth token present');
    
    try {
      const response: AxiosResponse<Game> = await this.api.post('/games', gameData);
      return response.data;
    } catch (error: any) {
      // Additional error handling specific to create game endpoint
      if (error.response?.status === 409) {
        throw new Error('A game with this name and type already exists for this company.');
      }
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Token may be expired or invalid.');
      }
      if (error.response?.status === 403) {
        throw new Error('Forbidden. You do not have permission to create games.');
      }
      throw error;
    }
  }

  // Delete a game
  async deleteGame(gameData: DeleteGameRequest): Promise<void> {
    // Check if auth token is present before making the request
    if (!this.hasAuthToken()) {
      console.warn('⚠️ No auth token present when calling deleteGame');
      throw new Error('Authentication required. Please login first.');
    }

    console.log('🎯 Making deleteGame request with auth token present');
    
    try {
      await this.api.delete('/games', {
        data: gameData
      });
    } catch (error: any) {
      // Additional error handling specific to delete game endpoint
      if (error.response?.status === 404) {
        throw new Error('Game not found or already deleted.');
      }
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Token may be expired or invalid.');
      }
      if (error.response?.status === 403) {
        throw new Error('Forbidden. You do not have permission to delete games.');
      }
      if (error.response?.status === 409) {
        throw new Error('Cannot delete game. It may have existing registration data.');
      }
      throw error;
    }
  }

  // Get statistics for a specific game
  async getGameStatistics(game: string, gameType: string, company: string): Promise<GameStatistics> {
    try {
      const registrations = await this.getRegistrations(game, gameType, company);
      
      if (registrations.length === 0) {
        return {
          game,
          game_type: gameType,
          totalPlayers: 0,
          todayPlayers: 0,
          lastUpdateDate: ''
        };
      }

      // Calculate total players
      const totalPlayers = registrations.reduce((sum, reg) => sum + reg.players, 0);
      
      // Get today's date in YYYY-MM-DD format
      const today = new Date().toISOString().split('T')[0];
      
      // Find today's registration data
      const todayRegistration = registrations.find(reg => reg.date === today);
      const todayPlayers = todayRegistration ? todayRegistration.players : 0;
      
      // Get the most recent date from registrations
      const sortedRegistrations = registrations.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      const lastUpdateDate = sortedRegistrations[0]?.date || '';

      return {
        game,
        game_type: gameType,
        totalPlayers,
        todayPlayers,
        lastUpdateDate
      };
    } catch (error) {
      console.error(`Error fetching statistics for ${game} (${gameType}):`, error);
      return {
        game,
        game_type: gameType,
        totalPlayers: 0,
        todayPlayers: 0,
        lastUpdateDate: ''
      };
    }
  }

  // Get aggregated registrations across all games for a company
  async getAggregatedRegistrations(company: string): Promise<AggregatedRegistration[]> {
    // Check if auth token is present before making the request
    if (!this.hasAuthToken()) {
      console.warn('⚠️ No auth token present when calling getAggregatedRegistrations');
      throw new Error('Authentication required. Please login first.');
    }

    console.log('🎯 Making getAggregatedRegistrations request with auth token present');
    
    try {
      // Get all games first
      const games = await this.getGames(company);
      
      if (games.length === 0) {
        return [];
      }

      // Fetch registrations for all games in parallel
      const allRegistrationsPromises = games.map(game => 
        this.getRegistrations(game.game, game.game_type, company)
          .catch(error => {
            console.warn(`Failed to fetch registrations for ${game.game}:`, error);
            return []; // Return empty array on error instead of failing the whole request
          })
      );

      const allRegistrations = await Promise.all(allRegistrationsPromises);

      // Aggregate by date
      const aggregatedMap = new Map<string, { totalPlayers: number; gamesCount: number }>();

      allRegistrations.forEach((gameRegistrations, gameIndex) => {
        gameRegistrations.forEach(registration => {
          const existing = aggregatedMap.get(registration.date);
          if (existing) {
            existing.totalPlayers += registration.players;
            // Only count unique games per date (avoid double counting)
            if (registration.players > 0) {
              existing.gamesCount += 1;
            }
          } else {
            aggregatedMap.set(registration.date, {
              totalPlayers: registration.players,
              gamesCount: registration.players > 0 ? 1 : 0
            });
          }
        });
      });

      // Convert to array and sort by date
      const aggregatedRegistrations: AggregatedRegistration[] = Array.from(aggregatedMap.entries())
        .map(([date, data]) => ({
          date,
          totalPlayers: data.totalPlayers,
          gamesCount: data.gamesCount
        }))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      return aggregatedRegistrations;
    } catch (error) {
      console.error('Error fetching aggregated registrations:', error);
      throw error;
    }
  }
}

export const apiService = new ApiService();

// Make API service available globally for debugging in development
if (process.env.NODE_ENV === 'development') {
  (window as any).apiService = apiService;
  (window as any).debugAuth = () => apiService.debugAuthState();
  console.log('🛠️ Debug helpers available: window.apiService, window.debugAuth()');
}
